import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Grid,
} from '@mui/material';
import {
  WavingHand,
  CheckCircle,
  ErrorOutline,
  HourglassEmpty,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  BookmarkBorder,
  PushPin,
  PushPinOutlined,
  Apple,
  Android,
} from '@mui/icons-material';
import { useAuth } from './AuthContext';
import { usePinnedApps } from '../utils/usePinnedApps';
import { CompactPipelineProgress } from './CompactPipelineProgress';
import { usePipelineContext } from '../contexts/PipelineContext';

const greetingFor = (hour: number, name?: string) => {
  let salutation = 'Hello';
  if (hour < 12) salutation = 'Good morning';
  else if (hour < 18) salutation = 'Good afternoon';
  else salutation = 'Good evening';
  return name ? `${salutation}, ${name}` : salutation;
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const statusColor = (status: string): string => {
  const s = (status || '').toLowerCase();
  if (/success/.test(s)) return '#16a34a';
  if (/fail/.test(s)) return '#dc2626';
  if (/cancel/.test(s)) return '#f59e0b';
  if (/queue|running|pending/.test(s)) return '#3b82f6';
  return '#94a3b8';
};

const statusLabel = (status: string): string => {
  const s = (status || '').toLowerCase();
  if (/success/.test(s)) return 'Success';
  if (/fail/.test(s)) return 'Failed';
  if (/cancel/.test(s)) return 'Cancelled';
  if (/queue|running|pending/.test(s)) return 'Running';
  return status || 'Unknown';
};

export const HomeOverviewComponent: React.FC = () => {
  const { userInfo, dashboardData } = useAuth();

  // Pinned apps come from a shared hook that listens for both same-tab and
  // cross-tab updates, so pinning a row in the activity feed below updates
  // this panel immediately without a refresh.
  const { pinned, togglePin } = usePinnedApps();

  // -------------------- Day summary --------------------
  // Two build sources are live in the app:
  //   1) dashboardData.recentBuilds — hydrated once on auth-context load
  //   2) PipelineContext.recentBuilds — refreshed every 8s by the activity
  //      feed below as new pipelines start / finish
  // Source #1 alone goes stale fast: a build kicked off after the user landed
  // on Home shows up in the feed (polled) but not in the day-summary tiles
  // (hydrated). Union both, dedupe by build id, so the tiles match the rows
  // the user is actually looking at.
  const { recentBuilds: livePipelineBuilds } = usePipelineContext();
  const recentBuilds: any[] = useMemo(() => {
    const seed: any[] = Array.isArray(dashboardData?.recentBuilds) ? dashboardData.recentBuilds : [];
    const byId = new Map<string, any>();
    for (const b of seed) {
      const key = String(b.id ?? b.buildId ?? `${b.composerHost}:${b.dateTimeStamp || b.dateTime}`);
      byId.set(key, b);
    }
    // PipelineContext rows win on conflict — they carry the freshest status /
    // phase from the most recent poll.
    for (const b of (livePipelineBuilds || [])) {
      const key = String(b.buildId ?? `${b.composerHost}:${b.dateTime}`);
      byId.set(key, b);
    }
    return Array.from(byId.values());
  }, [dashboardData, livePipelineBuilds]);

  const summary = useMemo(() => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    const todayBuilds = recentBuilds.filter(b => {
      const t = b.dateTime || b.dateTimeStamp || b.createdAt || b.updatedAt;
      if (!t) return false;
      const d = new Date(t);
      return isSameDay(d, now);
    });
    const yesterdayBuilds = recentBuilds.filter(b => {
      const t = b.dateTime || b.dateTimeStamp || b.createdAt || b.updatedAt;
      if (!t) return false;
      const d = new Date(t);
      return isSameDay(d, yesterday);
    });

    const countSuccess = (arr: any[]) =>
      arr.filter(b => /success/i.test(b.status || b.pipelineStatus || '')).length;
    const countFailed = (arr: any[]) =>
      arr.filter(b => /fail/i.test(b.status || b.pipelineStatus || '')).length;
    const countRunning = (arr: any[]) =>
      arr.filter(b => /queue|running|pending/i.test(b.status || b.pipelineStatus || '')).length;

    const successToday = countSuccess(todayBuilds);
    const failedToday = countFailed(todayBuilds);
    const runningToday = countRunning(todayBuilds);
    // Success rate counts only TERMINAL builds (success + failed) so a day
    // where everything is still running doesn't show as 0% — it shows "—".
    const terminalToday = successToday + failedToday;
    const successRateToday = terminalToday === 0 ? null : (successToday / terminalToday) * 100;
    const yesterdayTerminal = countSuccess(yesterdayBuilds) + countFailed(yesterdayBuilds);
    const yesterdaySuccessRate = yesterdayTerminal === 0
      ? null
      : (countSuccess(yesterdayBuilds) / yesterdayTerminal) * 100;

    let trend: 'up' | 'down' | 'flat' | null = null;
    if (successRateToday !== null && yesterdaySuccessRate !== null) {
      const delta = successRateToday - yesterdaySuccessRate;
      if (Math.abs(delta) < 1) trend = 'flat';
      else trend = delta > 0 ? 'up' : 'down';
    }

    return {
      total: todayBuilds.length,
      success: successToday,
      failed: failedToday,
      running: runningToday,
      successRate: successRateToday,
      trend,
    };
  }, [recentBuilds]);

  // For each pinned host, capture every live pipeline (queued / running /
  // pending) we can see in the recentBuilds feed, plus the latest build
  // overall for the fallback summary. The auth context's recentBuilds feed
  // is already sorted desc by createdAt, so the first match per host wins
  // for "latest" and the order of liveBuilds matches the feed.
  const pinnedRows = useMemo(() => {
    return pinned.map(host => {
      const forHost = recentBuilds.filter(b => (b.composerHost || b.domain) === host);
      const latest = forHost[0];
      const liveBuilds = forHost.filter(b => {
        const s = (b.status || b.pipelineStatus || '').toLowerCase();
        return /queue|running|pending/.test(s);
      });
      return { host, latest, liveBuilds };
    });
  }, [pinned, recentBuilds]);

  const firstName = useMemo(() => {
    const n = userInfo?.given_name || userInfo?.name || userInfo?.preferred_username || '';
    return (n.split(' ')[0] || '').trim();
  }, [userInfo]);

  const hour = new Date().getHours();

  return (
    <Stack spacing={3}>
      {/* Greeting + day summary */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: '1px solid rgba(226, 232, 240, 0.8)',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.04) 0%, rgba(139, 92, 246, 0.04) 100%)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 52,
                height: 52,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                boxShadow: '0 6px 18px rgba(99, 102, 241, 0.35)',
              }}
            >
              <WavingHand />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>
                {greetingFor(hour, firstName)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {summary.total === 0
                  ? 'No builds yet today — looks like a quiet morning.'
                  : `${summary.total} build${summary.total !== 1 ? 's' : ''} today · ${summary.success} success · ${summary.failed} failed${summary.running > 0 ? ` · ${summary.running} in flight` : ''}`}
              </Typography>
            </Box>
          </Stack>

          <Grid container spacing={1.5} sx={{ maxWidth: { md: 560 } }}>
            <Grid item xs={6} sm={3}>
              <SummaryCard
                icon={<HourglassEmpty sx={{ fontSize: 18 }} />}
                value={summary.total}
                label="Total today"
                accent="#6366f1"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard
                icon={<CheckCircle sx={{ fontSize: 18 }} />}
                value={summary.success}
                label="Succeeded"
                accent="#16a34a"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard
                icon={<ErrorOutline sx={{ fontSize: 18 }} />}
                value={summary.failed}
                label="Failed"
                accent="#dc2626"
                pulse={summary.failed > 0}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard
                icon={
                  summary.trend === 'up' ? <TrendingUp sx={{ fontSize: 18 }} />
                  : summary.trend === 'down' ? <TrendingDown sx={{ fontSize: 18 }} />
                  : <TrendingFlat sx={{ fontSize: 18 }} />
                }
                value={summary.successRate !== null ? `${Math.round(summary.successRate)}%` : '—'}
                label="Success rate"
                accent={summary.trend === 'down' ? '#dc2626' : summary.trend === 'up' ? '#16a34a' : '#475569'}
                trendNote={
                  summary.trend === null || summary.successRate === null ? undefined
                  : summary.trend === 'flat' ? 'vs yesterday'
                  : `${summary.trend === 'up' ? '+' : ''}${Math.round((summary.successRate ?? 0) - (recentBuildsYesterdayRate(recentBuilds) ?? 0))}pt vs yesterday`
                }
              />
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      {/* Watch list / pinned apps */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: '1px solid rgba(226, 232, 240, 0.8)',
          backgroundColor: '#fff',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <BookmarkBorder sx={{ color: '#6366f1' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
              Watch list
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {pinned.length === 0
                ? 'Pin apps from the activity feed below to track them at a glance.'
                : `Tracking ${pinned.length} app${pinned.length !== 1 ? 's' : ''} — live status from the latest build.`}
            </Typography>
          </Box>
          {pinned.length > 0 && (
            <Chip
              size="small"
              label={`${pinned.length} pinned`}
              sx={{ fontWeight: 700, bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#4338ca' }}
            />
          )}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {pinned.length === 0 ? (
          <Box
            sx={{
              border: '2px dashed #cbd5e1',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              backgroundColor: '#f8fafc',
            }}
          >
            <PushPinOutlined sx={{ fontSize: 28, color: '#94a3b8', mb: 1 }} />
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
              No pinned apps yet
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
              Click the pin icon on any row in the activity feed below to add it here.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={1.5}>
            {pinnedRows.map(({ host, latest, liveBuilds }) => {
              const platform = (latest?.os || latest?.platform || latest?.osSpecification || '').toLowerCase();
              const isIOS = platform === 'ios';
              const isAndroid = platform === 'android';
              const status = latest?.status || latest?.pipelineStatus || '';
              const hasLive = liveBuilds.length > 0;
              return (
                <Grid key={host} item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: hasLive ? 'rgba(99, 102, 241, 0.4)' : '#e2e8f0',
                      backgroundColor: hasLive ? 'rgba(99, 102, 241, 0.03)' : 'background.paper',
                      transition: 'all 150ms ease',
                      '&:hover': {
                        borderColor: 'rgba(99, 102, 241, 0.5)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                      },
                    }}
                  >
                    {/* Header row */}
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: hasLive ? 1.25 : 0 }}>
                      <Avatar
                        variant="rounded"
                        sx={{ width: 32, height: 32, bgcolor: isIOS ? '#0f172a' : isAndroid ? '#3DDC84' : '#cbd5e1' }}
                      >
                        {isIOS ? <Apple sx={{ fontSize: 16 }} /> : isAndroid ? <Android sx={{ fontSize: 16 }} /> : null}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {host}
                          </Typography>
                          {hasLive && (
                            <Chip
                              size="small"
                              label={`${liveBuilds.length} live`}
                              sx={{
                                height: 18,
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                bgcolor: 'rgba(59, 130, 246, 0.14)',
                                color: '#1d4ed8',
                              }}
                            />
                          )}
                        </Stack>
                        {!hasLive && (
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColor(status) }} />
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {latest ? `${statusLabel(status)}${latest.version ? ` · v${latest.version}` : ''}` : 'No recent activity'}
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                      <Tooltip title="Unpin" arrow>
                        <IconButton size="small" onClick={() => togglePin(host)} sx={{ color: '#6366f1' }}>
                          <PushPin sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    {/* Live pipelines — one row per in-flight build, mirroring
                        the Recent Activity Feed's CompactPipelineProgress. */}
                    {hasLive && (
                      <Stack spacing={0.75}>
                        {liveBuilds.slice(0, 4).map(b => {
                          const buildPlatform = (b.os || b.platform || b.osSpecification || '').toLowerCase();
                          const buildStatus = b.status || b.pipelineStatus || '';
                          const phase = b.phase || '';
                          const pipelineId = b.pipelineId ? String(b.pipelineId) : `pinned-${b.id || `${host}-${phase}`}`;
                          const displayPlatform = buildPlatform === 'ios' ? 'iOS' : buildPlatform === 'android' ? 'Android' : buildPlatform;
                          return (
                            <Box
                              key={pipelineId}
                              sx={{
                                p: 1,
                                borderRadius: 1.5,
                                bgcolor: 'background.paper',
                                border: '1px solid #e2e8f0',
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" rowGap={0.5}>
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                                  {b.version && (
                                    <Chip
                                      size="small"
                                      label={`v${b.version}`}
                                      sx={{ height: 18, fontSize: '0.62rem', fontFamily: 'monospace', fontWeight: 700 }}
                                    />
                                  )}
                                  {displayPlatform && (
                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                                      {displayPlatform}
                                    </Typography>
                                  )}
                                  {b.submitter && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: 120,
                                      }}
                                    >
                                      · {b.submitter}
                                    </Typography>
                                  )}
                                </Stack>
                                {b.pipelineUrl && (
                                  <Tooltip title="Open in GitLab" arrow>
                                    <Typography
                                      component="a"
                                      href={b.pipelineUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="caption"
                                      sx={{
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                      }}
                                    >
                                      {b.pipelineId ? `#${b.pipelineId}` : 'view'}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Stack>
                              <CompactPipelineProgress
                                pipelineId={pipelineId}
                                domainId={host}
                                platform={buildPlatform}
                                initialStatus={buildStatus}
                                initialPhase={phase}
                              />
                            </Box>
                          );
                        })}
                        {liveBuilds.length > 4 && (
                          <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                            … and {liveBuilds.length - 4} more in flight
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>
    </Stack>
  );
};

// Helper for the trend note — computes yesterday's success rate using the same
// recentBuilds the day-summary memo uses. Pulled out so the JSX above stays
// readable.
function recentBuildsYesterdayRate(recentBuilds: any[]): number | null {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const ys = recentBuilds.filter(b => {
    const t = b.dateTime || b.dateTimeStamp || b.createdAt || b.updatedAt;
    if (!t) return false;
    const d = new Date(t);
    return isSameDay(d, yesterday);
  });
  // Mirror the today calc: divide by TERMINAL builds only so a day with
  // unfinished pipelines doesn't drag the rate down to 0%.
  const success = ys.filter(b => /success/i.test(b.status || b.pipelineStatus || '')).length;
  const failed = ys.filter(b => /fail/i.test(b.status || b.pipelineStatus || '')).length;
  const terminal = success + failed;
  if (terminal === 0) return null;
  return (success / terminal) * 100;
}

const SummaryCard: React.FC<{
  icon: React.ReactNode;
  value: number | string;
  label: string;
  accent: string;
  pulse?: boolean;
  trendNote?: string;
}> = ({ icon, value, label, accent, pulse, trendNote }) => (
  <Box
    sx={{
      px: 1.5,
      py: 1.25,
      borderRadius: 2,
      backgroundColor: '#fff',
      border: '1px solid #e2e8f0',
      minHeight: 76,
    }}
  >
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: accent, mb: 0.25 }}>
      {icon}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: accent,
          ...(pulse && { animation: 'pulse 1.8s infinite' }),
        }}
      >
        {label}
      </Typography>
    </Stack>
    <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
      {value}
    </Typography>
    {trendNote && (
      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.25 }}>
        {trendNote}
      </Typography>
    )}
  </Box>
);

export default HomeOverviewComponent;
